import { useAIImageAnalysis } from '@/modules/inventory/hooks/useAIImageAnalysis';
import { useAISpecLookup } from '@/modules/inventory/hooks/useAISpecLookup';
import { useAIDescription } from '@/modules/inventory/hooks/useAIDescription';
import { useAIComparables } from '@/modules/inventory/hooks/useAIComparables';
import { useAIBudgetCheck } from '@/modules/inventory/hooks/useAIUsage';
import { useAiFeatureFlag } from '@/modules/inventory/hooks/useAiFeatureFlag';
import { guardPaidOperation } from '@/modules/inventory/hooks/paidAiGuard';
import { useCan } from '@/modules/tenant/hooks/useTenantAuth';

interface UseWizardAIOptions {
  boatId: string | null;
}

/**
 * Wizard-scoped AI orchestration hook.
 *
 * Composes the existing AI operation hooks (image analysis, spec lookup,
 * description, comparables) plus budget check and feature flag behind a
 * single interface. Instantiated once by the canvas and passed to step
 * components via props to avoid duplicate hook instances.
 *
 * All AI hooks require a boatId. When no server draft exists yet, we pass
 * an empty string -- the hooks' internal mutations won't fire without a
 * valid boatId (the trigger is manual via `.mutate()`).
 */
export function useWizardAI({ boatId }: UseWizardAIOptions) {
  const featureFlag = useAiFeatureFlag();
  const budgetCheck = useAIBudgetCheck();

  // All AI hooks require a boatId. Pass empty string when no draft exists.
  // Hooks are safe: mutations require manual trigger via .mutate().
  const safeBoatId = boatId ?? '';

  // ACL-09 (#611, D-22) — every operation here is paid AI. Without
  // inventory.ai.generate each trigger is inert, and the budget check is
  // skipped inside useAIBudgetCheck (D-29).
  const canGenerate = useCan('inventory.ai.generate');
  const imageAnalysis = guardPaidOperation(useAIImageAnalysis(safeBoatId), canGenerate);
  const specLookup = guardPaidOperation(useAISpecLookup(safeBoatId), canGenerate);
  const description = guardPaidOperation(useAIDescription(safeBoatId), canGenerate);
  const comparables = guardPaidOperation(useAIComparables(safeBoatId), canGenerate);

  return {
    featureFlag,
    budgetCheck,
    imageAnalysis,
    specLookup,
    description,
    comparables,
    hasBoatId: !!boatId,
  };
}

export type WizardAI = ReturnType<typeof useWizardAI>;
